import { DatabaseId } from "../helpers/Id";
import { DataSourceData } from "../responses/TransactionEvent";
import { PopulatedAssetHolding } from "./AssetHoldingBase";

export enum SyncState {
  InProgress = "InProgress",
  Error = "Error",
  Init = "Init",
}

export enum FileImportState {
  InProgress = "InProgress",
  Error = "Error",
  Init = "Init",
}
export enum SyncWalletErrorDetailCode {
  /**
   * The provided API keys are not able to be used, e.g. invalid, expired etc.
   */
  INVALID_AUTH = "INVALID_AUTH",

  /**
   * The API rate limit of the external API was exceeded.
   */
  RATE_LIMIT = "RATE_LIMIT",

  CUSTOM = "CUSTOM",

  /**
   * The blockchain address is invalid.
   */
  INVALID_ADDRESS = "INVALID_ADDRESS",

  /**
   * Interaction with the external API caused an unknown failure. We call it
   * an 'Outage' but it's most likely our programming error.
   */
  OUTAGE = "OUTAGE",

  /**
   * The total number of transactions being synced would have exceeded one of
   * the limits in Syla.
   */
  LIMIT_EXCEEDED = "LIMIT_EXCEEDED",

  UNKNOWN = "UNKNOWN",
}

export enum SyncWalletAuthErrorReason {
  InvalidKey = "InvalidKey",
  InsufficientPermission = "InsufficientPermission",
  ExcessivePermission = "ExcessivePermission",
  Expired = "Expired",
  InvalidIpRestriction = "InvalidIpRestriction",
  ValidKeyNotUsable = "ValidKeyNotUsable",
}

export type SyncWalletAuthErrorDetails = {
  code: SyncWalletErrorDetailCode.INVALID_AUTH;
  subReason: SyncWalletAuthErrorReason;
};
export type SyncWalletCustomErrorDetails = {
  code: SyncWalletErrorDetailCode.CUSTOM;
  title: string;
  message: string;
};
export type SyncWalletRateLimitErrorDetails = {
  code: SyncWalletErrorDetailCode.RATE_LIMIT;
  createdMs: number;
  vendor?: string;
  retryAfterMs?: number;
};
export type SyncWalletUnknownError = {
  code: SyncWalletErrorDetailCode.UNKNOWN;
};
export type SyncWalletOutageError = { code: SyncWalletErrorDetailCode.OUTAGE };
export type SyncWalletInvalidAddressError = {
  code: SyncWalletErrorDetailCode.INVALID_ADDRESS;
};

export enum SyncWalletLimitType {
  RECORD = "RECORD",
  TRANSACTION = "TRANSACTION",
  DATA_SOURCE = "DATA_SOURCE",
}

export type SyncWalletLimitError = {
  code: SyncWalletErrorDetailCode.LIMIT_EXCEEDED;
  limitType: SyncWalletLimitType;
  attemptedCount: number;
};
export type SyncWalletErrorDetails =
  | SyncWalletAuthErrorDetails
  | SyncWalletRateLimitErrorDetails
  | SyncWalletCustomErrorDetails
  | SyncWalletInvalidAddressError
  | SyncWalletLimitError
  | SyncWalletOutageError
  | SyncWalletUnknownError;

export type WalletData = {
  apiKey?: string;
  secretKey?: string;
  passphrase?: string;
  addresses?: string[];
  customName?: string;
  fromDate?: Date;

  authorised?: boolean;

  allowSync: boolean;

  transactionCount?: number;

  // last sync date
  lastSynced?: Date;

  // last file upload date
  lastUploaded?: Date;
  lastFileImportStarted?: Date;
  fileImportState?: FileImportState;

  importMethod: WalletImportMethod;

  /**
   * This prop stores something to reference the most recent api imported ledger (e.g. BTC Markets ledger id, etc)
   * Typed string as we don't know the possible formats of ledger id/reference from the other data sources
   *
   * @deprecated use `syncCursor`
   */
  lastImportedLedgerReference?: string;

  createdAt?: Date;
  updatedAt?: Date;

  syncState?: SyncState;
  syncError?: SyncWalletErrorDetails;
};

export type PopulatedWallet = WalletData & {
  _id: DatabaseId;
  owner: DatabaseId;
  dataSource: DataSourceData & { _id: DatabaseId };
  holdings?: PopulatedAssetHolding[];

  /**
   * Defined if the current wallets are being requested.
   * Not defined if historical wallet balances are being requested.
   */
  isLocked?: boolean;
};

export enum WalletImportMethod {
  API = "API", // the wallet has a 'sync' configured
  CSV = "CSV", // the wallet is only configured to use file imports
}

export function getWalletName({
  wallet,
  dataSource,
}: {
  wallet: Pick<WalletData, "customName">;
  dataSource: Pick<DataSourceData, "name">;
}): string {
  return wallet?.customName || dataSource.name;
}
